// Selection box and sizer handle rendering on the map highlights canvas

import { Scale } from '@/services/canvas/Scale';
import { getMapAreaService } from '@/modules/map-editor/composables/useMapArea';
import { SIZER_POINTS } from '@/models/node/SIZER_POINTS';
import type { SizerPoint, CircleSettings } from '@/modules/Node';

interface SelectionState {
  x: number;
  y: number;
  width: number;
  height: number;
  border: boolean;
  borderWidth: number;
  multiSelected: boolean;
}

export function drawSelected(
  ctx: CanvasRenderingContext2D,
  state: SelectionState,
  zoomLevel: number,
): SizerPoint[] {
  const points: Array<{ x: number; y: number; position: { value: string }; pointer: string }> = [];
  const computedBorder = state.border ? state.borderWidth * 2 : 0;
  const rectWidth = state.width + computedBorder;
  const rectHeight = state.height + computedBorder;

  const point1 = { x: state.x - rectWidth / 2, y: state.y - rectHeight / 2, position: SIZER_POINTS.TOP_LEFT, pointer: 'nw-resize' };
  const point2 = { x: state.x + rectWidth / 2, y: state.y - rectHeight / 2, position: SIZER_POINTS.TOP_RIGHT, pointer: 'ne-resize' };
  const point3 = { x: state.x + rectWidth / 2, y: state.y + rectHeight / 2, position: SIZER_POINTS.BOTTOM_RIGHT, pointer: 'nw-resize' };
  const point4 = { x: state.x - rectWidth / 2, y: state.y + rectHeight / 2, position: SIZER_POINTS.BOTTOM_LEFT, pointer: 'ne-resize' };
  points.push(point1, point2, point3, point4);

  const mas = getMapAreaService();
  const mapX = mas.x;
  const mapY = mas.y;

  const DOT_RADIUS = 3;
  const dot = {
    ctx,
    x: 0,
    y: 0,
    radius: DOT_RADIUS * zoomLevel,
    startAngle: 0,
    endAngle: Math.PI * 2,
    counterclockwise: false,
    fillStyle: '#ffffff',
    strokeStyle: '#2750E2',
    lineWidth: 1 * zoomLevel,
  };

  ctx.beginPath();
  ctx.rect(
    Scale.x(point1.x + mapX, ctx.canvas.width / 2, zoomLevel),
    Scale.y(point1.y + mapY, ctx.canvas.height / 2, zoomLevel),
    rectWidth * zoomLevel,
    rectHeight * zoomLevel,
  );
  ctx.closePath();
  ctx.strokeStyle = '#2750E2';
  ctx.lineWidth = 1 * zoomLevel;
  ctx.stroke();

  const selectedPoints: SizerPoint[] = [];
  if (!state.multiSelected) {
    points.forEach(point => {
      point.x = Scale.x(point.x + mapX, ctx.canvas.width / 2, zoomLevel);
      point.y = Scale.y(point.y + mapY, ctx.canvas.height / 2, zoomLevel);
      const settings: SizerPoint = Object.assign({}, dot, point);
      selectedPoints.push(settings);
      drawCircle(settings);
    });
  }

  return selectedPoints;
}

function drawCircle(settings: CircleSettings): void {
  const { ctx, x, y, radius, startAngle, endAngle, counterclockwise, fillStyle, strokeStyle, lineWidth } = settings;

  ctx.fillStyle = fillStyle;
  ctx.strokeStyle = strokeStyle;
  ctx.lineWidth = lineWidth;

  ctx.beginPath();
  ctx.arc(x, y, radius, startAngle, endAngle, counterclockwise);
  ctx.closePath();

  ctx.fill();
  ctx.stroke();
}

export type { SelectionState };
